/**
 * Die Figurenplatzierung auf dem Brett.
 *
 * `BoardState` ist die Antwort auf „welche Figur steht wo" — und nichts weiter. Keine
 * Zugreihenfolge, keine Punkte, keine Uhr, keine Respawn-Pools, keine `pendingAction`:
 * das alles kommt in Phase 4 mit dem GameState dazu, der diesen Zustand umhüllt.
 *
 * **Unveränderlich.** Jede Änderung gibt einen neuen Zustand zurück, der Ausgangszustand
 * bleibt unberührt. Damit fällt `simulateAction` (§90) kostenlos heraus: ein
 * hypothetischer Folgezustand kann den echten nicht berühren.
 *
 * **Diese Funktionen prüfen Konsistenz, nicht Spielregeln.** Eine Figur auf ein besetztes
 * Feld zu setzen ist hier ein Programmierfehler und wirft — ob ein Zug *erlaubt* ist,
 * entscheidet die Regelschicht, vorher. Diese Trennung ist wichtig: sonst wanderten
 * Spielregeln in die Datenstruktur, und ein Push über den Brettrand (der eine Figur
 * entfernt, obwohl dort kein Feld ist) wäre nicht mehr ausdrückbar.
 *
 * Der Zustand kennt das Brett bewusst **nicht**. Ob eine Koordinate existiert, weiß
 * `board.ts`; hier geht es nur um Belegung. Das hält beide Seiten einfach und testbar.
 */
import { hexKey } from './hex.js';
export function emptyBoardState() {
    return { pieces: new Map(), occupancy: new Map(), captured: new Map() };
}
/**
 * Baut ein Brett aus flachen Listen — der Weg zurück aus der Datenbank (Phase 6).
 *
 * Geprüft wird, was den Zustand widersprüchlich machen würde: eindeutige Figuren-IDs über
 * Brett **und** Pool hinweg, und höchstens eine Figur je Feld. Eine korrupte Zeile soll laut
 * scheitern und nicht still eine halbe Partie ergeben.
 */
export function createBoardState(input) {
    let state = emptyBoardState();
    for (const piece of input.pieces) {
        state = placePiece(state, piece);
    }
    const captured = new Map();
    for (const piece of input.captured) {
        if (state.pieces.has(piece.id)) {
            throw new Error(`Figur '${piece.id}' liegt im Respawn-Pool und steht gleichzeitig auf dem Brett`);
        }
        if (captured.has(piece.id)) {
            throw new Error(`Figur '${piece.id}' liegt mehrfach im Respawn-Pool`);
        }
        captured.set(piece.id, piece);
    }
    return { pieces: state.pieces, occupancy: state.occupancy, captured };
}
/** Die geschlagenen Figuren dieses Spielers, in der Reihenfolge ihres Verlusts. */
export function capturedOf(state, owner) {
    return [...state.captured.values()].filter((piece) => piece.owner === owner);
}
export function allPieces(state) {
    return [...state.pieces.values()];
}
export function pieceAt(state, cell) {
    const id = state.occupancy.get(hexKey(cell));
    return id === undefined ? undefined : state.pieces.get(id);
}
export function pieceById(state, id) {
    return state.pieces.get(id);
}
export function piecesOf(state, owner) {
    return allPieces(state).filter((piece) => piece.owner === owner);
}
export function isOccupied(state, cell) {
    return state.occupancy.has(hexKey(cell));
}
/**
 * Die Figur zu dieser ID, oder ein Fehler.
 *
 * Eine unbekannte Figuren-ID ist ein Programmierfehler, kein illegaler Zug: der Aufrufer
 * hat eine ID erfunden oder eine veraltete benutzt. Stilles `undefined` würde daraus einen
 * Zug ohne Wirkung machen, und das fiele erst viel später auf.
 */
export function requirePiece(state, id) {
    const piece = state.pieces.get(id);
    if (piece === undefined) {
        throw new Error(`Keine Figur mit der ID '${id}' auf dem Brett`);
    }
    return piece;
}
function requireFree(state, cell) {
    const occupant = state.occupancy.get(hexKey(cell));
    if (occupant !== undefined) {
        throw new Error(`Feld (${cell.q}, ${cell.r}) ist besetzt durch '${occupant}'`);
    }
}
/** Setzt eine Figur auf ein freies Feld — etwa in der Aufstellung oder beim Respawn. */
export function placePiece(state, piece) {
    if (state.pieces.has(piece.id)) {
        throw new Error(`Eine Figur mit der ID '${piece.id}' steht bereits auf dem Brett`);
    }
    requireFree(state, piece.at);
    const pieces = new Map(state.pieces);
    const occupancy = new Map(state.occupancy);
    pieces.set(piece.id, piece);
    occupancy.set(hexKey(piece.at), piece.id);
    return { pieces, occupancy, captured: state.captured };
}
/**
 * Nimmt eine Figur **ganz** aus dem Spiel, ohne Respawn-Pool.
 *
 * Für Schläge ist `capturePiece` zuständig — eine geschlagene Figur ist nach §22 nicht
 * verloren. Diese Funktion bleibt für den Debug-Modus und für Sonderfälle.
 */
export function removePiece(state, id) {
    const piece = requirePiece(state, id);
    const pieces = new Map(state.pieces);
    const occupancy = new Map(state.occupancy);
    pieces.delete(id);
    occupancy.delete(hexKey(piece.at));
    return { pieces, occupancy, captured: state.captured };
}
/**
 * Schlägt eine Figur: vom Brett entfernt, in den Respawn-Pool ihres Besitzers (§22).
 *
 * Gilt für jede Form des Schlagens — CAPTURE wie Push über den Spielfeldrand.
 */
export function capturePiece(state, id) {
    const piece = requirePiece(state, id);
    const pieces = new Map(state.pieces);
    const occupancy = new Map(state.occupancy);
    const captured = new Map(state.captured);
    pieces.delete(id);
    occupancy.delete(hexKey(piece.at));
    captured.set(id, { id: piece.id, type: piece.type, owner: piece.owner });
    return { pieces, occupancy, captured };
}
/**
 * Setzt eine geschlagene Figur wieder ein.
 *
 * Dass das Feld in der **eigenen Startzone** liegen muss, prüft die Regelschicht
 * (`legal-respawns.ts`) — hier wird nur die Konsistenz gewahrt.
 */
export function respawnPiece(state, id, at) {
    const piece = state.captured.get(id);
    if (piece === undefined) {
        throw new Error(`Figur '${id}' liegt nicht im Respawn-Pool`);
    }
    requireFree(state, at);
    const pieces = new Map(state.pieces);
    const occupancy = new Map(state.occupancy);
    const captured = new Map(state.captured);
    captured.delete(id);
    pieces.set(id, { ...piece, at });
    occupancy.set(hexKey(at), id);
    return { pieces, occupancy, captured };
}
/**
 * Nimmt eine Figur **ganz** aus dem Spiel — vom Brett **oder** aus dem Respawn-Pool.
 *
 * Der Unterschied zu `removePiece`: die kennt nur Figuren auf einem Feld. Gebraucht wird beides
 * für den Debug-Modus (§60, Zyklus 11e), wo „entfernen" heißt: diese Figur gibt es in dieser
 * Partie nicht mehr. Im Spiel selbst kommt das nicht vor — ein Schlag legt die Figur in den Pool
 * (§22), auch der Stoß über den Rand (§18). Genau deshalb ist ein veränderter Figurensatz
 * regelwidrig (`docs/OPEN_QUESTIONS.md` Nr. 42) und nicht nur unerreichbar.
 */
export function discardPiece(state, id) {
    const placed = state.pieces.get(id);
    const pooled = state.captured.get(id);
    if (placed === undefined && pooled === undefined) {
        throw new Error(`Keine Figur mit der ID '${id}' — weder auf dem Brett noch im Pool`);
    }
    const pieces = new Map(state.pieces);
    const occupancy = new Map(state.occupancy);
    const captured = new Map(state.captured);
    if (placed !== undefined) {
        pieces.delete(id);
        occupancy.delete(hexKey(placed.at));
    }
    captured.delete(id);
    return { pieces, occupancy, captured };
}
/** Verschiebt eine Figur auf ein freies Feld. */
export function movePiece(state, id, to) {
    const piece = requirePiece(state, id);
    requireFree(state, to);
    const pieces = new Map(state.pieces);
    const occupancy = new Map(state.occupancy);
    occupancy.delete(hexKey(piece.at));
    pieces.set(id, { ...piece, at: to });
    occupancy.set(hexKey(to), id);
    return { pieces, occupancy, captured: state.captured };
}
//# sourceMappingURL=board-state.js.map